/**
 * What the Process editor shows (ALN-32, docs/pages/process.md): the API's shapes, the labels the
 * app uses for stages, drivers and inventory pools, and the BPMN extension that carries a lane's stage.
 * The extension equals apps/server/src/process/avero-moddle.ts (a unit test keeps them equal).
 */
export const AVERO_MODDLE = {
  name: "Avero",
  uri: "https://averovintner.app/schema/bpmn",
  prefix: "avero",
  xml: { tagAlias: "lowerCase" },
  associations: [],
  types: [
    { name: "LaneStage", extends: ["bpmn:Lane"], properties: [{ name: "stage", isAttr: true, type: "String" }] },
  ],
};

export const STAGE_LABELS: Record<string, string> = {
  sourcing: "Sourcing", farming: "Farming", receiving: "Receiving", crush_press: "Crush and press", fermentation: "Fermentation",
  aging: "Aging", blending: "Blending", bottling: "Bottling", warehouse: "Warehouse",
};
export const STAGES = Object.keys(STAGE_LABELS);

/** Labeled as on WIP and inventory (decided Oct 17 2026). */
export const INVENTORY_POOL_LABELS: Record<string, string> = {
  crop_in_progress: "Crop in progress", grapes: "Grapes", must_juice: "Must and juice", bulk_tank: "Bulk in tank", bulk_barrel: "Bulk in barrel",
  bottled_aging: "Bottled, aging", finished_goods: "Finished goods", packaging: "Packaging and dry goods",
};

export const DRIVER_LABELS: Record<string, string> = {
  ton: "ton", acre_month: "acre-month", tank_day: "tank-day", gallon: "gallon", gallon_month: "gallon-month", barrel_gallon_month: "barrel gallon-month",
  case: "case", case_month: "case-month", labor_hour: "labor hour", fixed: "month",
};

/** The zoom bar's lane buttons and the stages each frames ("Work order and close" frames the other pools). */
export const FRAMES = [
  { label: "Vineyard to crush", stages: ["sourcing", "farming", "receiving", "crush_press"] },
  { label: "Cellar", stages: ["fermentation", "aging", "blending"] },
  { label: "Bottling to sale", stages: ["bottling", "warehouse"] },
  { label: "Work order and close", stages: [] as string[] },
] as const;

/**
 * The Vintner module that records each seed activity (mockup ACT_MODULE, accepted as drawn, P4).
 * None is built yet in R1, so each shows "Planned" and no link.
 */
export const RECORDED_IN: Record<string, string> = {
  T_Farm: "Vineyard blocks", T_Source: "Grower contracts", T_Harvest: "Harvest plan", T_Haul: "Fruit intake", T_Receive: "Fruit intake",
  T_Crush: "Work orders", T_PressWhite: "Work orders", T_FermWhite: "Work orders", T_FermRed: "Work orders", T_PressRed: "Work orders", T_Rack: "Work orders",
  T_AgeTank: "Vessels and barrels", T_AgeBarrel: "Vessels and barrels", T_Blend: "Lab and quality (trials)", T_Bottle: "Bottling runs",
  T_Store: "Case goods", T_BottleAge: "Case goods",
  T_ImportQBO: "Monthly close: Import", T_MapPools: "Monthly close: Map", T_Allocate: "Monthly close: Allocate", T_Value: "Compliance and Monthly close: Review", T_PostJE: "Monthly close: Approve and post",
};

export interface Pool { id: string; name: string; kind: "labor" | "overhead" | "direct" | "excluded"; driver: string; capitalize: boolean; isActive: boolean; companyId: string; companyName: string; rateCents?: number | null }
export interface Step { bpmnElementId: string; name: string; overheadPoolId: string | null; laborPoolId: string | null; standardHours: number | null; inventoryPool: string | null }
export interface DraftSummary {
  revision: number; baseVersion: number; preparedBy: string[]; submittedBy: string | null; submittedById: string | null; submittedAt: string | null; submitNote: string | null;
  sentBack: { by: string; at: string | null; note: string } | null; updatedAt: string; updatedBy: string | null;
}
export interface ProcessView {
  id: string; name: string; version: number; isSample: boolean; bpmnXml: string; publishedAt: string | null; publishedBy: string | null; submittedBy: string | null;
  preparedBy: string[]; note: string | null; readOnly: boolean; steps: (Step & { stage: string; driver: string; capitalize: boolean })[];
  draft: DraftSummary | null; programsOnThisVersion: number; programsOnOlderVersions: number; pools: Pool[]; estimated: string[];
  costsHidden: boolean; canEdit: boolean; canApprove: boolean; canSendBack: boolean; selfApprovalAllowed: boolean; canStartEstimate: boolean;
}
export interface DraftView { revision: number; baseVersion: number; bpmnXml: string; steps: Step[]; preparedBy: string[]; updatedAt: string; updatedBy: string | null }
export interface VersionRow { version: number; isCurrent: boolean; isSample: boolean; publishedAt: string | null; publishedBy: string | null; submittedBy: string | null; preparedBy: string[]; note: string | null; programs: number }

/** "$28", or "$0.09" under $10 with cents (the mockup's rule). Cents in, possibly fractional. */
export function usd(cents: number): string {
  const dollars = cents / 100;
  const digits = Math.abs(dollars) < 10 && Math.round(dollars) !== dollars ? 2 : 0;
  return dollars.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: digits, maximumFractionDigits: digits });
}
/** "Maria", "Maria and Ryan", "Ana, Maria and Ryan". */
export const people = (names: string[]) => (names.length <= 1 ? names[0] ?? "" : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`);
export const dayLabel = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
export const dateLabel = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
export const timeLabel = (iso: string) => new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }).replace(/\s/g, " ");
/** Hours as typed: a number of zero or more, or empty; anything else is an error. */
export function parseHours(v: string): { ok: true; value: number | null } | { ok: false } {
  const t = v.trim();
  if (!t) return { ok: true, value: null };
  if (!/^\d+(\.\d+)?$/.test(t)) return { ok: false };
  const n = Number(t);
  return n >= 0 && n < 10000 ? { ok: true, value: n } : { ok: false };
}
